import {Poi, PoiGroup, Trip} from '../../react-app-env';
import ListViewGroup from '../listview/listviewgroup/ListViewGroup';
import ListViewItem from '../listview/listviewitem/ListViewItem';
import {getPoiCategoryIconCategory} from '../../utils/PoiCategoryUtils';
import ListView from '../listview/ListView';
import React from 'react';
import {Events, EventType} from '../../events/Events';
import SidebarScrollableContent from '../sidebar/SidebarScrollableContent';
import {DefaultItemDecorations} from '../listview/listviewitem/ListViewItem.types';
import {useHoveringPois} from '../context/HoveringPoisContext';
import {useTrip} from '../context/TripContext';

export interface ItineraryPoiListProps {
  trip: Trip;
}

function ItineraryPoiList() {
  const trip = useTrip();
  const hoveringPois = useHoveringPois();

  /**
   * Gets POI groups sorted by tripPosition.
   *
   * @return {PoiGroup[]} The sorted POI groups.
   */
  function getSortedPoiGroups(): PoiGroup[] {
    return trip.poiGroups.sort((groupA: PoiGroup, groupB: PoiGroup) => {
      return groupA.tripPosition - groupB.tripPosition;
    });
  }

  /**
   * Gets POI items by groupId.
   *
   * @param {string} groupId The POI group ID.
   * @return {Poi[]} The sorted POIs for the specified group.
   */
  function getSortedPoiItemsByGroupId(groupId: string): Poi[] {
    return trip.pois
               .filter(poi => poi.groupId === groupId)
               .sort((poiA: Poi, poiB: Poi) => {
                 return poiA.groupPosition - poiB.groupPosition;
               });
  }

  /**
   * Renders a POI group.
   *
   * @param {PoiGroup} poiGroup The POI group to render.
   */
  function renderPoiGroup(poiGroup: PoiGroup) {
    return <ListViewGroup key={poiGroup.id}
                          title={poiGroup.title}>
      {renderPoiGroupItems(poiGroup)}
    </ListViewGroup>;
  }

  /**
   * Renders POI items for a given POI group.
   *
   * @param {PoiGroup} poiGroup The POI group.
   */
  function renderPoiGroupItems(poiGroup: PoiGroup) {
    const poiItems = getSortedPoiItemsByGroupId(poiGroup.id);
    return poiItems.map((poi) => {
      const isHovering = hoveringPois.some(hoveringPoi => hoveringPoi.id === poi.id);

      return <ListViewItem key={poi.id}
                           title={poi.title}
                           iconCategory={getPoiCategoryIconCategory(poi.category)}
                           decoration={DefaultItemDecorations.CHEVRON}
                           topAffix={poi.transit ? { label: poi.transit } : undefined}
                           bottomAffix={poi.duration ? { label: poi.duration } : undefined}
                           hovering={isHovering}
                           onClick={() => Events.publish(EventType.SELECT_POI, poi)}
                           onMouseEnter={() => Events.publish(EventType.ON_POIS_HOVERING, [poi])}
                           onMouseLeave={() => Events.publish(EventType.ON_POIS_HOVERING, [])}/>;
    });
  }

  return (
      <SidebarScrollableContent>
        <ListView>
          {getSortedPoiGroups().map(poiGroup => renderPoiGroup(poiGroup))}
        </ListView>
      </SidebarScrollableContent>
  );
}

export default ItineraryPoiList;
